import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type HoldingsResponse } from '../lib/api.js';
import { MoneyText, formatCompact } from '../components/MoneyText.js';
import { Sheet, PrimaryButton, fieldStyle } from '../components/Sheet.js';
import { Card, ErrorNote, Row, Skeleton } from './Home.js';

interface CampaignDto {
  id: string;
  name: string;
  hueSlot: number | null;
  status: string;
  target: { money: { amount: number; currency: 'INR' }; date: string | null } | null;
  corpus: { money: { amount: number; currency: 'INR' }; asOf: string; isEstimate: boolean } | null;
  progress: number | null;
  holdingCount: number;
  version: number;
}

/** Goals (docs/SCREEN-SPECS.md §5): one card per goal — corpus-desc, never a table. */
export function Goals() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState<CampaignDto | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api.get<CampaignDto[]>('/campaigns'),
  });

  if (isPending) return <Skeleton />;
  if (error) return <ErrorNote message={(error as Error).message} />;

  return (
    <div>
      <h1 className="page-title">Goals</h1>

      {data.length === 0 && (
        <Card title="Give your investments a purpose">
          <p style={{ font: 'var(--type-subhead)', color: 'var(--color-label-secondary)' }}>
            Campaigns earmark holdings for the things they are for — Siya, Aashi, retirement.
          </p>
        </Card>
      )}

      {data.map((c) => (
        <Card key={c.id} title="">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ font: 'var(--type-headline)' }}>
              <HueDot slot={c.hueSlot} /> {c.name}
            </span>
            {c.corpus && <MoneyText variant="compact" value={c.corpus.money} />}
          </div>
          {c.target && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <div
                role="presentation"
                style={{ height: 6, borderRadius: 3, background: 'var(--color-fill-subtle)' }}
              >
                <div
                  style={{
                    height: 6, borderRadius: 3,
                    width: `${Math.min(100, (c.progress ?? 0) * 100)}%`,
                    background: c.hueSlot ? `var(--color-slot-${c.hueSlot})` : 'var(--color-tint)',
                  }}
                />
              </div>
              <div style={{ font: 'var(--type-footnote)', color: 'var(--color-label-secondary)', marginTop: 4 }}>
                {Math.round((c.progress ?? 0) * 100)}% of {formatCompact(c.target.money.amount)}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'var(--space-3)' }}>
            <span className="row-meta">
              {c.holdingCount} holding{c.holdingCount === 1 ? '' : 's'}
            </span>
            <button
              onClick={() => setAssigning(c)}
              style={{ border: 'none', background: 'none', color: 'var(--color-tint)', font: 'var(--type-subhead)', cursor: 'pointer', minHeight: 44 }}
            >
              Add holdings →
            </button>
          </div>
        </Card>
      ))}

      {/* Inline add row at list end — thumb zone, in scroll flow (ergonomics law). */}
      <button
        onClick={() => setCreating(true)}
        style={{
          width: '100%', minHeight: 50, border: 'none', borderRadius: 'var(--radius-card)',
          background: 'var(--color-fill-subtle)', color: 'var(--color-tint)',
          font: 'var(--type-headline)', cursor: 'pointer',
        }}
      >
        + New campaign
      </button>

      {creating && (
        <CreateCampaignSheet
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
          }}
        />
      )}
      {assigning && (
        <AssignSheet
          campaign={assigning}
          onClose={() => setAssigning(null)}
          onDone={() => {
            setAssigning(null);
            void queryClient.invalidateQueries();
          }}
        />
      )}
    </div>
  );
}

function HueDot({ slot }: { slot: number | null }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block', width: 10, height: 10, borderRadius: 5,
        background: slot ? `var(--color-slot-${slot})` : 'var(--color-label-tertiary)',
      }}
    />
  );
}

function CreateCampaignSheet({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [target, setTarget] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api.post('/campaigns', {
        name,
        // Rupees typed by a human become integer paise here, at the boundary.
        targetPaise: target ? Math.round(Number(target) * 100) : null,
      }),
    onSuccess: onCreated,
  });

  return (
    <Sheet title="New campaign" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <input style={fieldStyle} placeholder="Name — e.g. Siya" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
        <input style={fieldStyle} placeholder="Target amount in ₹ (optional)" inputMode="numeric" value={target} onChange={(e) => setTarget(e.target.value.replace(/[^\d]/g, ''))} />
        {create.isError && <ErrorNote message={(create.error as Error).message} />}
        <PrimaryButton type="submit" disabled={create.isPending || !name.trim()}>
          {create.isPending ? 'Creating…' : 'Create campaign'}
        </PrimaryButton>
      </form>
    </Sheet>
  );
}

function AssignSheet({ campaign, onClose, onDone }: { campaign: CampaignDto; onClose: () => void; onDone: () => void }) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { data } = useQuery({
    queryKey: ['holdings'],
    queryFn: () => api.get<HoldingsResponse>('/holdings'),
  });
  const assign = useMutation({
    mutationFn: () => api.post(`/campaigns/${campaign.id}/holdings`, { holdingIds: [...selected] }),
    onSuccess: onDone,
  });

  const unassigned = (data?.sections ?? []).flatMap((s) => s.holdings).filter((h) => h.campaignId === null);

  return (
    <Sheet title={`Add holdings to ${campaign.name}`} onClose={onClose}>
      {unassigned.length === 0 && (
        <p style={{ font: 'var(--type-subhead)', color: 'var(--color-label-secondary)' }}>
          Everything is already in a campaign.
        </p>
      )}
      <div style={{ maxHeight: '50dvh', overflowY: 'auto', marginBottom: 'var(--space-4)' }}>
        {unassigned.map((h) => (
          <label key={h.id} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', minHeight: 44, borderBottom: '1px solid var(--color-separator)' }}>
            <input
              type="checkbox"
              checked={selected.has(h.id)}
              onChange={(e) => {
                const next = new Set(selected);
                if (e.target.checked) next.add(h.id);
                else next.delete(h.id);
                setSelected(next);
              }}
              style={{ width: 20, height: 20 }}
            />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', font: 'var(--type-subhead)' }}>
              {h.scheme.name || h.scheme.isin}
            </span>
            {h.valuation && <MoneyText variant="compact" value={h.valuation.money} />}
          </label>
        ))}
      </div>
      {assign.isError && <ErrorNote message={(assign.error as Error).message} />}
      <PrimaryButton onClick={() => assign.mutate()} disabled={assign.isPending || selected.size === 0}>
        {assign.isPending ? 'Assigning…' : `Add ${selected.size || ''} holding${selected.size === 1 ? '' : 's'}`}
      </PrimaryButton>
    </Sheet>
  );
}
